/**
 * Device State Actions Configuration
 *
 * Defines all state-based actions for device lifecycle management.
 * Each action includes:
 * - Unique ID
 * - Display label and description
 * - State condition (deviceStatus + procurementStatus + provisioningStatus)
 * - Form component to render
 * - UI metadata (icon, button variant)
 */

import {
  Power,
  XCircle,
  CheckCircle,
  Activity,
  AlertTriangle,
  Trash2,
  Truck,
  Package,
} from 'lucide-react'
import {
  ShipDeviceComposer,
  ReceiveDeviceComposer,
  ProvisionDeviceComposer,
  RetryProvisionComposer,
  TestDeviceComposer,
  TestFailDeviceComposer,
  ActivateDeviceComposer,
  FaultDeviceComposer,
  RetireDeviceComposer,
  CancelProvisionComposer,
  CompleteTestProvisionComposer,
  CompleteLiveProvisionComposer,
} from '@/forms/sensory'
import type { DeviceStateAction } from '@/utils/deviceStateMatcher'

/**
 * Device State Actions Configuration Array
 *
 * IMPORTANT: Order matters! The first matching action will be used.
 * Uses simplified status model:
 * - deviceStatus: single source of truth for lifecycle state
 * - provisioningStatus: provisioning-specific state (requested/success/failed/deprovisioned)
 * - provisioningType: 'test' | 'live'
 */
export const DEVICE_STATE_ACTIONS: DeviceStateAction[] = [
  // 1. Mark as Shipped
  // State: Device ordered, not yet shipped
  {
    id: 'mark-as-shipped',
    label: 'Mark as Shipped',
    description: 'Record shipping details',
    icon: Truck,
    variant: 'default',
    condition: {
      deviceStatus: 'ordered',
    },
    component: ShipDeviceComposer,
  },

  // 2. Mark as Received
  // State: Device shipped, not yet received
  {
    id: 'mark-as-received',
    label: 'Mark as Received',
    description: 'Confirm device delivery',
    icon: Package,
    variant: 'default',
    condition: {
      deviceStatus: 'shipped',
    },
    component: ReceiveDeviceComposer,
  },

  // 3. Provision for Testing
  // State: Device received, ready for test provisioning
  {
    id: 'provision-for-testing',
    label: 'Provision for Testing',
    description: 'Provision device to test network',
    icon: Power,
    variant: 'default',
    condition: {
      deviceStatus: 'received',
    },
    component: ProvisionDeviceComposer,
  },

  // 4. Cancel Provisioning (Pending)
  // State: Device in testing, provisioning requested but not complete
  {
    id: 'cancel-provision',
    label: 'Cancel Provisioning',
    description: 'Cancel pending provisioning request',
    icon: XCircle,
    variant: 'outline',
    condition: {
      deviceStatus: 'testing',
      provisioningStatus: 'requested',
    },
    component: CancelProvisionComposer,
  },

  // 5. Confirm Provisioning Success (Requested)
  // State: Device in testing, provisioning requested for test flow
  {
    id: 'mark-requested-provision-success',
    label: 'Mark as Success',
    description: 'Confirm request completed and move to tested',
    icon: CheckCircle,
    variant: 'default',
    condition: {
      deviceStatus: 'testing',
      provisioningStatus: 'requested',
      provisioningType: 'test',
    },
    component: CompleteTestProvisionComposer,
  },

  // 6. Confirm Live Provisioning Success (Requested)
  // State: Device in live status, provisioning requested for live flow
  {
    id: 'mark-requested-live-provision-success',
    label: 'Mark as Success',
    description: 'Confirm live request completed',
    icon: CheckCircle,
    variant: 'default',
    condition: {
      deviceStatus: 'live',
      provisioningStatus: 'requested',
      provisioningType: 'live',
    },
    component: CompleteLiveProvisionComposer,
  },

  // 7. Retry Provisioning (Failed)
  // State: Device in testing, provisioning failed
  {
    id: 'retry-provision',
    label: 'Reprovision',
    description: 'Re-send the provisioning request to the prov service',
    icon: XCircle,
    variant: 'destructive',
    condition: {
      deviceStatus: 'testing',
      // Reprovision is available after a failed OR a deprovisioned attempt.
      provisioningStatus: ['failed', 'deprovisioned'],
    },
    component: RetryProvisionComposer,
  },

  // 7b. Deprovision (Failed) — back the device out of provisioning, return to received
  {
    id: 'deprovision',
    label: 'Deprovision',
    description: 'Remove the failed provisioning and return the device to received',
    icon: XCircle,
    variant: 'outline',
    condition: {
      deviceStatus: 'testing',
      provisioningStatus: 'failed',
    },
    component: CancelProvisionComposer,
  },

  // 8. Test Passed (provisioning succeeded → user tested the device successfully)
  // State: Device in testing, provisioning succeeded (test type)
  {
    id: 'mark-as-tested',
    label: 'Mark Test Passed',
    description: 'Record the device passed testing — ready for activation',
    icon: CheckCircle,
    variant: 'default',
    condition: {
      deviceStatus: 'testing',
      provisioningStatus: 'success',
      provisioningType: 'test',
    },
    component: TestDeviceComposer,
  },

  // 8b. Test Failed (provisioning succeeded but the device failed testing)
  // Captures a failure reason. Distinct from a provisioning failure.
  {
    id: 'mark-test-failed',
    label: 'Mark Test Failed',
    description: 'Record that the device failed testing, with a reason',
    icon: XCircle,
    variant: 'destructive',
    condition: {
      deviceStatus: 'testing',
      provisioningStatus: 'success',
      provisioningType: 'test',
    },
    component: TestFailDeviceComposer,
  },

  // 9. Activate Device
  // State: Device available for activation (either tested or already available)
  {
    id: 'activate-device',
    label: 'Activate Device',
    description: 'Deploy to live network',
    icon: Activity,
    variant: 'default',
    condition: {
      deviceStatus: ['tested', 'available'],
    },
    component: ActivateDeviceComposer,
  },

  // 10. Mark as Faulty
  // State: Device live and operational, or failed testing (failed → faulty)
  {
    id: 'mark-as-faulty',
    label: 'Mark as Faulty',
    description: 'Report device fault',
    icon: AlertTriangle,
    variant: 'outline',
    condition: {
      deviceStatus: ['live', 'failed'],
    },
    component: FaultDeviceComposer,
  },

  // 11. Retire Device from Live
  // State: Device live and being decommissioned
  {
    id: 'retire-device-from-live',
    label: 'Retire Device',
    description: 'Decommission and remove from inventory',
    icon: Trash2,
    variant: 'destructive',
    condition: {
      deviceStatus: 'live',
    },
    component: RetireDeviceComposer,
  },

  // 12. Retire Device from Faulty / Returned / Failed
  {
    id: 'retire-device',
    label: 'Retire Device',
    description: 'Remove from inventory',
    icon: Trash2,
    variant: 'destructive',
    condition: {
      deviceStatus: ['faulty', 'returned', 'failed'],
    },
    component: RetireDeviceComposer,
  },
]
